import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowRight,
    ArrowUpRight,
    Plus,
    Check,
    ShieldCheck,
    Ruler,
    Palette,
    Truck,
    Wrench,
    Sparkles,
    MapPin,
    Phone,
    Mail,
    Clock,
    Download,
} from "lucide-react";
import { BRAND, STATS, FEATURES, MODELS, COLORS, GALLERY, FAQS } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const featureIcons = [ShieldCheck, Sparkles, Ruler, Palette, Truck, Wrench];

export function Hero() {
    return (
        <section id="top" className="pt-[120px] pb-20 lg:pt-[170px] lg:pb-28">
            <div className="container-p">
                <div className="grid items-center gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-7 float-up">
                        <span className="eyebrow">Aluminijske ograde · Proizvođač · BiH</span>
                        <h1 className="mt-6 text-[clamp(2.6rem,7vw,5.6rem)] font-[Cabinet_Grotesk] leading-[0.95]">
                            Aluminijska
                            <br />
                            ograda <span className="text-[hsl(var(--accent))]">po</span>
                            <br />
                            <span className="text-[hsl(var(--accent))]">mjeri.</span>
                        </h1>

                        <p className="mt-8 max-w-xl text-[1.02rem] leading-[1.7] text-[hsl(var(--muted-ink))]">
                            Moderna <strong className="text-[hsl(var(--ink))]">alu ograda</strong> direktno od proizvođača. Bez održavanja, dugovječna i dostupna u <strong className="text-[hsl(var(--ink))]">217 RAL boja</strong>. Izrada po mjeri, besplatno planiranje i dostava širom BiH.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <Link to="/#kontakt" className="btn btn-accent" data-testid="hero-cta-quote">
                                Neobavezujuća ponuda <ArrowRight className="h-4 w-4" />
                            </Link>
                            <a href="#modeli" className="btn btn-ghost" data-testid="hero-cta-models">
                                Pogledaj modele
                            </a>
                            <Link to="/katalog" className="btn btn-ghost" data-testid="hero-cta-catalog">
                                <Download className="h-4 w-4" /> Katalog (PDF)
                            </Link>
                        </div>

                        <div className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-[hsl(var(--line))] pt-8">
                            {STATS.map((s) => (
                                <div key={s.label}>
                                    <div className="font-[Cabinet_Grotesk] text-[2.4rem] font-black leading-none tracking-tight">{s.value}</div>
                                    <div className="mt-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-ink))]">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative lg:col-span-5">
                        <div className="relative mx-auto max-w-[520px]">
                            <div className="relative bg-gradient-to-br from-[#f7f7f8] to-[#dedde0] aspect-[4/5] overflow-hidden">
                                <span className="corner-tl" aria-hidden />
                                <span className="corner-br" aria-hidden />
                                <img src={MODELS[5].img} alt="Premium aluminijska ograda Galactic" className="h-full w-full object-contain p-6" />
                                <div className="absolute bottom-6 left-6 bg-[hsl(var(--ink))] px-5 py-2.5 text-xs font-bold tracking-[0.16em] text-white">GALACTIC</div>
                            </div>
                            <div className="mt-5 flex items-center justify-between">
                                <span className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-[hsl(var(--muted-ink))]">Model Galactic</span>
                                <Link to="/modeli/galactic" className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[hsl(var(--ink))] transition-colors hover:text-[hsl(var(--accent))]">
                                    Detalji <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function WhyAluminium() {
    return (
        <section id="zasto" className="py-24 lg:py-32 border-t border-[hsl(var(--line))]">
            <div className="container-p">
                <div className="grid gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5 reveal">
                        <span className="eyebrow">Zašto aluminij</span>
                        <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.4rem)] leading-[0.98]">
                            Stabilno.<br />Moderno.<br />
                            <span className="text-[hsl(var(--accent))]">Zauvijek.</span>
                        </h2>
                        <p className="mt-7 max-w-md text-[hsl(var(--muted-ink))] leading-[1.7]">
                            <strong className="text-[hsl(var(--ink))]">Aluminijska ograda</strong> spaja sve što moderan sistem mora imati: stabilnost, dugovječnost i bezvremenski dizajn. Za razliku od drveta ili čelika, alu ograda je praktično bez održavanja i ne rđa. Kvalitetna plastifikacija odolijeva i ekstremnim vremenskim uslovima i ostaje postojana u boji desetljećima.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid sm:grid-cols-2 border-l border-t border-[hsl(var(--line))]">
                            {FEATURES.map((f, i) => {
                                const Icon = featureIcons[i] || Check;
                                return (
                                    <div key={f.title} className="reveal border-r border-b border-[hsl(var(--line))] bg-white p-7 transition-colors hover:bg-[hsl(var(--paper-2))]" data-testid={`feature-${i}`}>
                                        <Icon className="h-7 w-7 text-[hsl(var(--accent))]" strokeWidth={1.6} />
                                        <h3 className="mt-5 font-[Cabinet_Grotesk] text-[1.2rem] font-bold tracking-tight">{f.title}</h3>
                                        <p className="mt-2 text-[0.92rem] leading-relaxed text-[hsl(var(--muted-ink))]">{f.body}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function Models() {
    return (
        <section id="modeli" className="py-24 lg:py-32 bg-[hsl(var(--paper-2))] border-t border-[hsl(var(--line))]">
            <div className="container-p">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <div className="reveal max-w-2xl">
                        <span className="eyebrow">Naši modeli</span>
                        <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.4rem)] leading-[0.98]">
                            Aluminijske ograde <br />
                            <span className="text-[hsl(var(--accent))]">modeli.</span>
                        </h2>
                    </div>
                    <p className="lg:max-w-md text-[hsl(var(--muted-ink))] leading-[1.7] reveal">
                        Od klasičnih lamela do modernih premium sistema — svaki model se izrađuje individualno po mjeri. Kliknite na model za tehničke detalje i izvedbe.
                    </p>
                </div>

                <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {MODELS.map((m) => (
                        <Link to={`/modeli/${m.id}`} key={m.id} className="model-card reveal" data-testid={`model-${m.id}`}>
                            <div className="thumb">
                                <img src={m.img} alt={`Aluminijska ograda ${m.name}`} loading="lazy" />
                                <span className="nr">{m.n}</span>
                                <span className="name-tag">{m.name}</span>
                            </div>
                            <div className="flex flex-1 flex-col p-7">
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="font-[Cabinet_Grotesk] text-[1.6rem] font-black tracking-tight uppercase leading-tight">{m.name}</h3>
                                    <ArrowUpRight className="h-5 w-5 shrink-0 text-[hsl(var(--ink))] mt-2" />
                                </div>
                                <p className="mt-3 text-[0.76rem] font-bold uppercase tracking-[0.14em] text-[hsl(var(--accent))]">{m.tag}</p>
                                <p className="mt-4 text-[0.92rem] leading-[1.65] text-[hsl(var(--muted-ink))]">{m.desc}</p>
                                <span className="mt-auto pt-6 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-[hsl(var(--ink))]">
                                    Pogledaj detalje <ArrowRight className="h-4 w-4" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function Colors() {
    return (
        <section id="boje" className="py-24 lg:py-32 band-dark">
            <div className="container-p">
                <div className="grid items-end gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-6 reveal">
                        <span className="eyebrow">RAL karta K7</span>
                        <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.4rem)] leading-[0.98]">
                            217 boja. <br />
                            <span className="text-[hsl(var(--accent))]">Jedna ograda.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 reveal">
                        <p className="text-[rgba(255,255,255,0.72)] leading-[1.7]">
                            Svaka aluminijska ograda plastificira se u vašoj željenoj boji. Izaberite iz najpopularnijih tonova — ili iz ukupno <strong className="text-white">217 RAL boja</strong> K7 palete.
                        </p>
                    </div>
                </div>

                <div className="mt-14 grid grid-cols-8 gap-2 sm:gap-3">
                    {COLORS.map((c) => (
                        <div
                            key={c.ral}
                            className="swatch reveal"
                            style={{ background: c.hex }}
                            data-testid={`swatch-${c.ral.replace(/\s/g, "")}`}
                        >
                            <div className="meta">
                                <div className="ral">{c.ral}</div>
                                <div className="name">{c.name}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <p className="mt-10 text-sm text-[rgba(255,255,255,0.6)]">+ dodatnih 209 tonova prema RAL karti K7, dostupno na upit.</p>
            </div>
        </section>
    );
}

export function Gallery() {
    return (
        <section id="reference" className="py-24 lg:py-32 border-t border-[hsl(var(--line))]">
            <div className="container-p">
                <div className="grid items-end gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-6 reveal">
                        <span className="eyebrow">Reference</span>
                        <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.4rem)] leading-[0.98]">
                            Realizovani <br />
                            <span className="text-[hsl(var(--accent))]">projekti.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-6 text-[hsl(var(--muted-ink))] leading-[1.7] reveal">
                        Izbor iz naših realizovanih projekata — privatne porodične kuće, bazeni, balkonske ograde i poslovni objekti širom Bosne i Hercegovine.
                    </p>
                </div>

                <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {GALLERY.map((g, i) => (
                        <div key={i} className="tile reveal" style={{ aspectRatio: "4/3" }} data-testid={`gallery-${i}`}>
                            <img src={g.src} alt={g.label} loading="lazy" />
                            <span className="label">{g.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function Faq() {
    const [openIdx, setOpenIdx] = useState(0);
    return (
        <section id="faq" className="py-24 lg:py-32 bg-[hsl(var(--paper-2))] border-t border-[hsl(var(--line))]">
            <div className="container-p">
                <div className="grid gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5 reveal">
                        <span className="eyebrow">Česta pitanja</span>
                        <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.4rem)] leading-[0.98]">FAQ.</h2>
                        <p className="mt-7 max-w-md text-[hsl(var(--muted-ink))] leading-[1.7]">
                            Odgovori na najčešće postavljana pitanja o našim aluminijskim ogradama. Ne vidite odgovor? <Link to="/#kontakt" className="font-bold text-[hsl(var(--accent))] hover:underline">Pišite nam direktno.</Link>
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        {FAQS.map((f, i) => {
                            const open = i === openIdx;
                            return (
                                <div key={f.q} className="faq-item reveal" data-testid={`faq-${i}`}>
                                    <button className="faq-trigger" aria-expanded={open} onClick={() => setOpenIdx(open ? -1 : i)}>
                                        <span>{f.q}</span>
                                        <Plus className="chev h-6 w-6 shrink-0" />
                                    </button>
                                    <div className={`faq-panel ${open ? "open" : ""}`}>
                                        <div className="faq-panel-inner">{f.a}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

function InfoRow({ icon, label, value, href, testid }) {
    const Inner = (
        <>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[hsl(var(--ink))] text-white">{icon}</span>
            <span>
                <span className="block text-[0.68rem] uppercase tracking-[0.2em] font-bold text-[hsl(var(--muted-ink))]">{label}</span>
                <span className="mt-1 block font-[Cabinet_Grotesk] font-bold text-[hsl(var(--ink))]">{value}</span>
            </span>
        </>
    );
    return href ? (
        <a href={href} className="flex items-center gap-4 group hover:text-[hsl(var(--accent))]" data-testid={testid}>{Inner}</a>
    ) : (
        <div className="flex items-center gap-4">{Inner}</div>
    );
}

export function ContactForm({ defaultProduct = "" }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        product: defaultProduct,
        message: "",
        consent: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Molimo popunite obavezna polja.");
            return;
        }
        if (!form.consent) {
            toast.error("Potrebna je saglasnost za obradu podataka.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/contact`, form);
            toast.success("Hvala! Vaš upit je zaprimljen — javljamo se u roku od 24h.");
            setForm({ name: "", email: "", phone: "", product: defaultProduct, message: "", consent: true });
        } catch (err) {
            console.error(err);
            toast.error("Greška pri slanju. Pokušajte ponovo ili nas nazovite.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="kontakt" className="py-24 lg:py-32 border-t border-[hsl(var(--line))]">
            <div className="container-p">
                <div className="grid gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5 reveal">
                        <span className="eyebrow">Kontakt & upit</span>
                        <h2 className="mt-6 text-[clamp(2rem,4.5vw,3.4rem)] leading-[0.98]">
                            Zatražite <br />
                            <span className="text-[hsl(var(--accent))]">ponudu.</span>
                        </h2>
                        <p className="mt-7 max-w-md text-[hsl(var(--muted-ink))] leading-[1.7]">
                            Odgovor u roku od 24 sata. Besplatno i neobavezujuće savjetovanje sa našim stručnjacima za aluminijske ograde.
                        </p>

                        <div className="mt-10 space-y-5">
                            <InfoRow icon={<MapPin className="h-4 w-4" />} label="Adresa" value={BRAND.address} />
                            <InfoRow icon={<Phone className="h-4 w-4" />} label="Telefon" value={BRAND.phone} href={BRAND.phoneHref} testid="contact-phone" />
                            <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testid="contact-email" />
                            <InfoRow icon={<Clock className="h-4 w-4" />} label="Radno vrijeme" value={BRAND.hours} />
                        </div>
                    </div>

                    <form onSubmit={submit} className="lg:col-span-7 bg-white border border-[hsl(var(--line))] p-6 sm:p-10 reveal" data-testid="contact-form">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div className="field">
                                <label>Ime i prezime *</label>
                                <input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="contact-input-name" />
                            </div>
                            <div className="field">
                                <label>E-mail *</label>
                                <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-input-email" />
                            </div>
                            <div className="field">
                                <label>Telefon</label>
                                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-input-phone" />
                            </div>
                            <div className="field">
                                <label>Proizvod / model</label>
                                <select value={form.product} onChange={(e) => update("product", e.target.value)} data-testid="contact-input-product">
                                    <option value="">Izaberite model...</option>
                                    {MODELS.map((m) => (
                                        <option key={m.id} value={m.name}>{m.name}</option>
                                    ))}
                                    <option value="Opšti upit">Opšti upit / savjet</option>
                                </select>
                            </div>
                        </div>

                        <div className="field mt-5">
                            <label>Poruka *</label>
                            <textarea required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Opišite ukratko: dužina ograde, visina, boja, lokacija..." data-testid="contact-input-message" />
                        </div>

                        <label className="mt-6 flex items-start gap-3 text-[0.85rem] text-[hsl(var(--muted-ink))]">
                            <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-1 h-4 w-4 accent-[hsl(var(--accent))]" data-testid="contact-input-consent" />
                            <span>Saglasan/na sam da se moji podaci koriste za obradu mog upita. Detalje o privatnosti pročitajte u politici privatnosti.</span>
                        </label>

                        <div className="mt-7 flex items-center justify-between gap-4">
                            <p className="text-xs uppercase tracking-[0.16em] font-semibold text-[hsl(var(--muted-ink))]">Odgovor u roku od 24h</p>
                            <button type="submit" disabled={submitting} className="btn btn-accent disabled:opacity-60" data-testid="contact-submit">
                                {submitting ? "Slanje..." : "Pošalji upit"} <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
